import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { createRangePatterns, createUnitPatterns, createThemePatterns, createColorPatterns } from "./ControlPatternMixins";

const VALID_THEMES = ['flat', 'rounded', 'pill', 'material', 'neon'] as const;

const THEME_ALIASES: Record<string, string> = {
  'simple': 'flat',
  'clean': 'flat',
  'circle': 'rounded',
  'round': 'rounded',
};

export class StepperCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Range patterns (min, max, step, value, range X to Y)
    for (const [regex, handler] of createRangePatterns()) {
      this.commandPatterns.set(regex, handler);
    }

    // Unit patterns
    for (const [regex, handler] of createUnitPatterns()) {
      this.commandPatterns.set(regex, handler);
    }

    // Theme patterns
    for (const [regex, handler] of createThemePatterns([...VALID_THEMES], THEME_ALIASES)) {
      this.commandPatterns.set(regex, handler);
    }

    // Color patterns
    for (const [regex, handler] of createColorPatterns(['buttonColor', 'textColor'])) {
      this.commandPatterns.set(regex, handler);
    }

    // Size
    this.commandPatterns.set(
      /(?:set )?size (?:to )?(sm|small|md|medium|lg|large)/i,
      (match) => {
        const sizeMap: Record<string, string> = {
          sm: 'sm', small: 'sm', md: 'md', medium: 'md', lg: 'lg', large: 'lg',
        };
        return { properties: { size: sizeMap[match[1].toLowerCase()] || 'md' } };
      }
    );

    // Decimals
    this.commandPatterns.set(
      /(?:set )?decimals? (?:to |= ?)?(\d+)/i,
      (match) => ({ properties: { decimals: parseInt(match[1]) } })
    );

    // Label
    this.commandPatterns.set(
      /(?:set )?label (?:to )?["']?(.+?)["']?\s*$/i,
      (match) => ({ properties: { label: match[1].trim() } })
    );

    // Long press toggle
    this.commandPatterns.set(
      /enable (?:long\s*press|acceleration)/i,
      () => ({ properties: { longPressAcceleration: true } })
    );

    this.commandPatterns.set(
      /disable (?:long\s*press|acceleration)/i,
      () => ({ properties: { longPressAcceleration: false } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.STEPPER && this.hasKeywords(command);
  }

  private hasKeywords(command: string): boolean {
    const keywords = [
      "stepper", "step", "increment", "decrement", "range", "min", "max",
      "value", "unit", "theme", "size", "decimals", "label", "color",
      "flat", "rounded", "pill", "material", "neon",
      "acceleration", "long press",
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const patternMatch = this.matchPatterns(trimmed, component);
      if (patternMatch?.properties) {
        changes.properties = { ...changes.properties, ...patternMatch.properties };
      }
      if (patternMatch?.style) {
        changes.style = { ...changes.style, ...patternMatch.style };
      }
    }

    if (!changes.properties || Object.keys(changes.properties).length === 0) {
      return {
        success: false,
        error: "Could not understand stepper command",
        suggestions: await this.getExampleCommands(ComponentType.STEPPER),
      };
    }

    const validation = this.validateChanges(changes, component);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    return {
      success: true,
      command: {
        type: this.determineCommandType(changes),
        changes,
        confidence: this.calculateConfidence(command, changes),
        description: this.generateDescription(changes, component),
      },
    };
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    const props = (component.properties || {}) as Record<string, unknown>;
    const min = (changes.properties?.min as number) ?? (props.min as number) ?? 0;
    const max = (changes.properties?.max as number) ?? (props.max as number) ?? 100;
    const step = (changes.properties?.step as number) ?? (props.step as number) ?? 1;
    const decimals = changes.properties?.decimals as number | undefined;

    if (min >= max) {
      return { valid: false, error: "Min must be less than max" };
    }
    if (step <= 0) {
      return { valid: false, error: "Step must be greater than 0" };
    }
    if (decimals != null && decimals < 0) {
      return { valid: false, error: "Decimals must be 0 or greater" };
    }

    return super.validateChanges(changes, component);
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      "Set range 0 to 100",
      "Step 5",
      "Value 50",
      "Unit degrees",
      "Theme rounded",
      "Size large",
      "Decimals 2",
      "Set label to Temperature",
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = (component.properties || {}) as Record<string, unknown>;
    const currentTheme = (props.theme as string) ?? 'flat';

    suggestions.push("Set range 0 to 255");
    suggestions.push("Step 5");

    const otherThemes = VALID_THEMES.filter(t => t !== currentTheme);
    if (otherThemes.length > 0) {
      suggestions.push(`Theme ${otherThemes[0]}`);
    }

    suggestions.push("Unit degrees");
    suggestions.push("Size large");
    suggestions.push("Decimals 2");

    return suggestions.slice(0, 6);
  }
}

export const stepperCommandHandler = new StepperCommandHandler();
